import i18nFactory from '@yandex-int/i18n';
import cn from 'classnames';
import { debounce, forEach, isEqual } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { copyTextToClipboard } from '../../service/Clipboard/Clipboard';
import { PredictorHelpers } from '../../service/PredictorHelpers/PredictorHelpers';
import { CopyTooltipStatus, GroupType } from '../../typings/domain';
import { GdBidModifierDemographicInput } from '../../typings/graphql-grid';
import { PlatformType, ReachResultValue, TextPredictor } from '../../typings/predictor';
import { PredictorAdvisor } from '../PredictorAdvisor/PredictorAdvisor';
import { PredictorHeader } from '../PredictorHeader/PredictorHeader';
import { PredictorId } from '../PredictorId/PredictorId';
import { PredictorItem } from '../PredictorItem/PredictorItem';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';
import { ScopedTooltip } from '../TooltipDecorator/ScopedTooltip';

import './Predictor.css';
import * as keyset from './Predictor.i18n';

export interface PredictorProps {
    cls?: string;
    text?: TextPredictor[];
    metrics?: TextPredictor[];
    groupType?: GroupType;
    listRecommendation?: TextPredictor[];
    isLoadingRecommendation?: boolean;
    id?: string;
    isLoading?: boolean;
    sum?: ReachResultValue;
    detailed?: ReachResultValue;
    platform?: PlatformType;
    error?: string;
    recommendationError?: string;
    isGrowPerformanceEnabled?: boolean;
    isExpanderOpened: boolean;
    banner?: TextPredictor;
    otsBasic?: ReachResultValue;
    otsDetailed?: ReachResultValue;
    copyText?: string;
    hideTitle?: boolean;
    adjustmentDemography?: GdBidModifierDemographicInput[];

    updatePredictor?: () => void;
    onOpenRecommendation?: () => void;
    onCloseRecommendation?: () => void;
}

export enum HeightMods {
    AUTO = 'auto',
    WINDOW = 'window'
}

export interface PredictorState {
    copyTooltipStatus: CopyTooltipStatus;
    heightMod: HeightMods;
}

const i18n = i18nFactory(keyset);

const BLOCK = 'predictor';
const COPIED_TOOLTIP_TIMEOUT = 1000;

export class Predictor extends React.Component<PredictorProps, PredictorState> {
    updateHeightWithDebounce = debounce(this.updateHeight.bind(this), 150);
    private copiedTooltipAnchor = React.createRef<StyledLink>();
    private elem = React.createRef<HTMLDivElement>();

    constructor(props: PredictorProps) {
        super(props);

        this.state = {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            heightMod: HeightMods.AUTO
        };
    }

    componentDidMount() {
        this.update();
        window.addEventListener('resize', this.updateHeightWithDebounce);
    }

    componentDidUpdate(prevProps: PredictorProps) {
        if (
            !isEqual(this.props.text, prevProps.text) ||
            !isEqual(this.props.groupType, prevProps.groupType) ||
            !isEqual(this.props.metrics, prevProps.metrics) ||
            (this.props.platform && !isEqual(this.props.platform, prevProps.platform)) ||
            (this.props.adjustmentDemography &&
                !isEqual(this.props.adjustmentDemography, prevProps.adjustmentDemography))
        ) {
            this.update();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateHeightWithDebounce);
    }

    render() {
        const { text, cls, copyText } = this.props;
        const { copyTooltipStatus, heightMod } = this.state;

        return (
            <div
                className={cn(
                    Bem.cls(BLOCK),
                    Bem.cls({
                        block: BLOCK,
                        elem: 'inventory',
                        mods: {
                            height: heightMod
                        }
                    }),
                    cls
                )}
                ref={this.elem}
            >
                {this.getHeader()}
                {this.getAdvisor()}
                <div className={Bem.cls(BLOCK, 'content')}>
                    <div className={Bem.cls(BLOCK, 'content-title')}>{i18n('predictor-settings')}</div>
                    {this.getBanners()}
                    <div className={Bem.cls(BLOCK, 'items')}>
                        {text &&
                            text.map((item, index) => {
                                return <PredictorItem key={index} title={item.title} value={item.value} />;
                            })}
                    </div>
                    {this.getMetrics()}
                </div>
                <div className={Bem.cls(BLOCK, 'footer')}>
                    <StyledLink
                        theme={StyledLinkTheme.grey}
                        cls={Bem.cls(BLOCK, 'link-copy')}
                        onClick={this.onCopy}
                        ref={this.copiedTooltipAnchor}
                    >
                        <Shape2
                            icon={Shape2Icon.copy}
                            theme={Shape2Theme.default}
                            width={16}
                            height={16}
                            cls={Bem.cls(BLOCK, 'icon-copy')}
                        />
                        {copyText ? copyText : i18n('copy-link')}
                    </StyledLink>
                    <ScopedTooltip
                        anchor={this.copiedTooltipAnchor.current}
                        theme={copyTooltipStatus === CopyTooltipStatus.error ? 'error' : 'normal'}
                        view={'classic'}
                        tone={'default'}
                        to={'right'}
                        size={'s'}
                        visible={copyTooltipStatus !== CopyTooltipStatus.hidden}
                    >
                        {copyTooltipStatus === CopyTooltipStatus.error ? i18n('copy-error') : i18n('copied')}
                    </ScopedTooltip>
                </div>
            </div>
        );
    }

    private getBanners = () => {
        const { banner } = this.props;

        if (!banner) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'items')}>
                <PredictorItem title={banner.title} value={banner.value} />
            </div>
        );
    };

    private getMetrics = () => {
        const { metrics } = this.props;

        if (metrics && metrics.length) {
            return (
                <>
                    <div className={Bem.cls(BLOCK, 'metrics-title')}>{i18n('audience-and-metrika')}</div>
                    <div className={Bem.cls(BLOCK, 'items')}>
                        {metrics.map((item, index) => {
                            return <PredictorItem key={index} title={item.title} value={item.value} />;
                        })}
                    </div>
                </>
            );
        }

        return null;
    };

    private getHeader = () => {
        const { otsBasic, otsDetailed, sum, detailed, isLoading, error, groupType, hideTitle, id } = this.props;
        let ots = null;

        if (groupType === GroupType.CPM_OUTDOOR) {
            ots = (
                <PredictorHeader
                    hideTitle={true}
                    titleBlock={i18n('ots')}
                    sumText={PredictorHelpers.getBasedText(otsBasic)}
                    detailedText={PredictorHelpers.getDetailedText(otsDetailed)}
                    isLoading={isLoading}
                    percent={PredictorHelpers.getPercent(otsBasic, otsDetailed)}
                    error={error}
                />
            );
        }

        return (
            <>
                <PredictorHeader
                    id={id}
                    hideTitle={hideTitle}
                    titleBlock={this.getBlockTitle()}
                    sumText={PredictorHelpers.getBasedText(sum)}
                    detailedText={PredictorHelpers.getDetailedText(detailed)}
                    isLoading={isLoading}
                    percent={PredictorHelpers.getPercent(sum, detailed)}
                    error={error}
                />
                {ots}
            </>
        );
    };

    private getBlockTitle = () => {
        const { groupType, id, hideTitle } = this.props;

        let text;

        switch (groupType) {
            case GroupType.CPM_INDOOR:
                text = i18n('ots');
                break;
            case GroupType.CPM_OUTDOOR:
                text = i18n('reach-title-ots');
                break;
            default:
                text = undefined;
        }

        if (!text) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'title-block')}>
                <span className={Bem.cls(BLOCK, 'title-block-text')}>{text}</span>
                {/* Если скрыт title, то id тоже пропадает. */}
                {/* В таком кейсе отображаем id ниже. */}
                {hideTitle && <PredictorId id={id} />}
            </div>
        );
    };

    private getAdvisor = () => {
        const {
            isLoadingRecommendation,
            id,
            listRecommendation,
            recommendationError,
            onOpenRecommendation,
            isExpanderOpened,
            onCloseRecommendation,
            isGrowPerformanceEnabled
        } = this.props;

        return (
            <PredictorAdvisor
                id={id}
                isLoading={isLoadingRecommendation}
                error={recommendationError}
                text={listRecommendation}
                onOpen={onOpenRecommendation}
                isExpanderOpened={isExpanderOpened}
                onClose={onCloseRecommendation}
                isGrowPerformanceEnabled={isGrowPerformanceEnabled}
            />
        );
    };

    private update = () => {
        const { updatePredictor } = this.props;

        if (updatePredictor) {
            updatePredictor();
        }

        this.updateHeight();
    };

    private onCopy = () => {
        copyTextToClipboard(this.getCopyText()).then(() => this.showCopiedTooltip());
    };

    private showCopiedTooltip = () => {
        this.setState({ copyTooltipStatus: CopyTooltipStatus.success });

        setTimeout(() => {
            this.setState({ copyTooltipStatus: CopyTooltipStatus.hidden });
        }, COPIED_TOOLTIP_TIMEOUT);
    };

    private getCopyText = () => {
        const { text, metrics, banner } = this.props;

        let metricsText = this.getText(metrics);
        const audienceText = this.getText(text);
        const bannersText = banner ? `${this.getText([banner])}\n` : '';

        metricsText = metricsText ? `${i18n('audience-and-metrika')}\n${metricsText}` : '';

        const delim = audienceText && metricsText ? '\n\n' : '';

        return `${i18n('predictor-settings')}\n${bannersText}${audienceText}${delim}${metricsText}`;
    };

    private getText = (textData?: TextPredictor[]) => {
        if (textData && textData.length) {
            return `${textData
                .map(item => {
                    const val = typeof item.value === 'string' ? item.value : item.value.join(',');

                    return `\n${item.title}\n${val}`;
                })
                .join('\n')}`;
        }

        return '';
    };

    /**
     * Обновляет высоту стакана
     */
    private updateHeight() {
        if (!this.elem || !this.elem.current) {
            return;
        }

        const domElemHeight = this.elem.current.clientHeight;
        const windowHeight = window.innerHeight;
        const contentElem = this.elem.current.children[2] as HTMLElement;
        let inventoryHeight = 0;

        // мы не можем обернуть содержимое в еще один div, потому-что в IE будет все плохо
        forEach(this.elem.current.children, (el: HTMLElement) => {
            inventoryHeight += el.offsetHeight;
        });

        inventoryHeight = inventoryHeight - contentElem.offsetHeight + contentElem.scrollHeight;

        if (domElemHeight > inventoryHeight) {
            if (windowHeight - 180 > inventoryHeight) {
                this.setState({ heightMod: HeightMods.AUTO });
            } else {
                this.setState({ heightMod: HeightMods.WINDOW });
            }
        }
    }
}
